'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface DiffPreviewProps {
  before: string;
  after: string;
  className?: string;
  showLineNumbers?: boolean;
}

interface DiffLine {
  type: 'unchanged' | 'removed' | 'added';
  content: string;
  lineNumber?: number;
}

/**
 * A simple diff preview component that shows before/after changes.
 * Uses a basic line-by-line diff algorithm.
 */
export function DiffPreview({
  before,
  after,
  className,
  showLineNumbers = false,
}: DiffPreviewProps) {
  const diffLines = useMemo(() => computeDiff(before, after), [before, after]);

  return (
    <div className={cn("rounded-lg border border-gray-200 overflow-hidden", className)}>
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">Changes Preview</span>
      </div>
      <div className="p-3 font-mono text-sm overflow-x-auto">
        {diffLines.map((line, index) => (
          <div
            key={index}
            className={cn(
              "flex items-start",
              line.type === 'removed' && "bg-red-50 text-red-800",
              line.type === 'added' && "bg-green-50 text-green-800",
              line.type === 'unchanged' && "text-gray-600"
            )}
          >
            {showLineNumbers && (
              <span className="w-8 text-right pr-2 text-gray-400 select-none flex-shrink-0">
                {line.lineNumber || ''}
              </span>
            )}
            <span className={cn(
              "w-4 text-center flex-shrink-0",
              line.type === 'removed' && "text-red-500",
              line.type === 'added' && "text-green-500"
            )}>
              {line.type === 'removed' ? '-' : line.type === 'added' ? '+' : ' '}
            </span>
            <span className="whitespace-pre-wrap break-words flex-1">
              {line.content || ' '}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Side-by-side diff view for comparing before and after.
 */
export function SideBySideDiff({
  before,
  after,
  className,
}: DiffPreviewProps) {
  return (
    <div className={cn("grid grid-cols-2 gap-4", className)}>
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-red-50 px-3 py-2 border-b border-red-200">
          <span className="text-xs font-medium text-red-700">Before</span>
        </div>
        <div className="p-3 text-sm bg-white">
          <p className="whitespace-pre-wrap text-gray-700">{before}</p>
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-green-50 px-3 py-2 border-b border-green-200">
          <span className="text-xs font-medium text-green-700">After</span>
        </div>
        <div className="p-3 text-sm bg-white">
          <p className="whitespace-pre-wrap text-gray-700">{after}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline diff that highlights changes within the text.
 */
export function InlineDiff({
  before,
  after,
  className,
}: DiffPreviewProps) {
  const { removedWords, addedWords, unchangedParts } = useMemo(
    () => computeWordDiff(before, after),
    [before, after]
  );

  return (
    <div className={cn("rounded-lg border border-gray-200 p-3 text-sm", className)}>
      <div className="whitespace-pre-wrap">
        {unchangedParts.map((part, index) => (
          <span key={index}>
            {index > 0 && removedWords[index - 1] && (
              <span className="bg-red-100 text-red-800 line-through mx-0.5">
                {removedWords[index - 1]}
              </span>
            )}
            {index > 0 && addedWords[index - 1] && (
              <span className="bg-green-100 text-green-800 mx-0.5">
                {addedWords[index - 1]}
              </span>
            )}
            <span className="text-gray-700">{part}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Simple line-by-line diff computation.
 */
function computeDiff(before: string, after: string): DiffLine[] {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const result: DiffLine[] = [];

  // Simple LCS-based diff
  const lcs = computeLCS(beforeLines, afterLines);

  let beforeIndex = 0;
  let afterIndex = 0;
  let lineNumber = 1;

  for (const [bi, ai] of lcs) {
    // Add removed lines
    while (beforeIndex < bi) {
      result.push({
        type: 'removed',
        content: beforeLines[beforeIndex],
      });
      beforeIndex++;
    }

    // Add added lines
    while (afterIndex < ai) {
      result.push({
        type: 'added',
        content: afterLines[afterIndex],
        lineNumber: lineNumber++,
      });
      afterIndex++;
    }

    // Add unchanged line
    result.push({
      type: 'unchanged',
      content: beforeLines[bi],
      lineNumber: lineNumber++,
    });
    beforeIndex++;
    afterIndex++;
  }

  // Add remaining removed lines
  while (beforeIndex < beforeLines.length) {
    result.push({
      type: 'removed',
      content: beforeLines[beforeIndex],
    });
    beforeIndex++;
  }

  // Add remaining added lines
  while (afterIndex < afterLines.length) {
    result.push({
      type: 'added',
      content: afterLines[afterIndex],
      lineNumber: lineNumber++,
    });
    afterIndex++;
  }

  return result;
}

/**
 * Compute Longest Common Subsequence indices.
 */
function computeLCS(a: string[], b: string[]): [number, number][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  // Build LCS table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find LCS
  const result: [number, number][] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift([i - 1, j - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return result;
}

/**
 * Simple word-level diff for inline display.
 */
function computeWordDiff(before: string, after: string): {
  removedWords: string[];
  addedWords: string[];
  unchangedParts: string[];
} {
  const beforeWords = before.split(/(\s+)/);
  const afterWords = after.split(/(\s+)/);

  // Find common prefix
  let prefixEnd = 0;
  while (
    prefixEnd < beforeWords.length &&
    prefixEnd < afterWords.length &&
    beforeWords[prefixEnd] === afterWords[prefixEnd]
  ) {
    prefixEnd++;
  }

  // Find common suffix
  let suffixStart = 0;
  while (
    suffixStart < beforeWords.length - prefixEnd &&
    suffixStart < afterWords.length - prefixEnd &&
    beforeWords[beforeWords.length - 1 - suffixStart] === afterWords[afterWords.length - 1 - suffixStart]
  ) {
    suffixStart++;
  }

  const prefix = beforeWords.slice(0, prefixEnd).join('');
  const suffix = beforeWords.slice(beforeWords.length - suffixStart).join('');
  const removedMiddle = beforeWords.slice(prefixEnd, beforeWords.length - suffixStart).join('');
  const addedMiddle = afterWords.slice(prefixEnd, afterWords.length - suffixStart).join('');

  return {
    removedWords: removedMiddle ? [removedMiddle] : [],
    addedWords: addedMiddle ? [addedMiddle] : [],
    unchangedParts: [prefix, suffix].filter(Boolean),
  };
}
